import { ApiError, api } from '../../lib/api'

/**
 * Activation endpoints — `Features/Auth/Activate/Endpoint.cs`.
 *
 *   confirmEmail  -> GET /api/activate?id={userId}&t={token}   -> { IsArabic, AccountStateMessage }
 *   getStatus     -> GET /api/activate/{status}                -> { IsArabic, AccountStateMessage }
 *
 * `confirmEmail` IS NOT A READ. It confirms the email address, may auto-approve the
 * account depending on the company's activation settings, and writes a user status log.
 * It stays a GET because that is what the link in every already-sent activation email
 * does, and those emails cannot be recalled (R1).
 *
 * The token is single-use only once it succeeds, so a failed call can be retried by
 * opening the link again — which is what makes it safe to run this from the client.
 *
 * WHY THE MESSAGES ARE FETCHED rather than bundled. Each of these screens is one
 * sentence, and this app already ships a copy of all five in `strings.*.js`. But they are
 * SERVER messages: an administrator editing `Common/Localization/ar-SA.json` changes what
 * the product says, and a bundled copy would silently stop matching (W6). The bundled
 * strings stay as the fallback for the moment before the answer arrives, so the screen
 * never renders empty or flashes.
 */

/** The `ActivationStatus` enum values, as the route expects them. */
export const ACTIVATION_STATUS = {
  waitingUser: 'WaitingUser',
  waitingAdmin: 'WaitingAdmin',
  expired: 'Expired',
  suspended: 'Suspended',
  resigned: 'Resigned',
}

export async function confirmEmail({ id, token }, signal) {
  const query = `?id=${encodeURIComponent(id)}&t=${encodeURIComponent(token)}`
  const data = await api.get(`/api/activate${query}`, { signal })
  return data?.AccountStateMessage ?? ''
}

/**
 * @param {keyof typeof ACTIVATION_STATUS} state
 * @returns {Promise<string|null>} the server's message, or null if it could not be read —
 *          the caller then shows its bundled copy rather than nothing.
 */
export async function getStatusMessage(state, signal) {
  const status = ACTIVATION_STATUS[state]
  if (!status) return null

  try {
    const data = await api.get(`/api/activate/${status}`, { signal })
    return data?.AccountStateMessage || null
  } catch (error) {
    if (error instanceof ApiError) return null
    throw error
  }
}
